import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Loader2, Calendar } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, addDays } from 'date-fns';
// DTC-G G3: route the booking-flow "tomorrow" anchor through the
// canonical clock so booking decisions are deterministic under
// `clock.setFixed(...)`. The `useState(new Date())` calendar-month
// initial state at L46 is migrated as G-Optional-Low for testability.
import { clock } from '@/utils/clock';

interface DayStat {
  date: string;
  // CORE-AR: utilisation is internal load and is not sent to the public calendar.
  isBookable: boolean;
  reason?: string;
}

interface TimeSlot {
  startAt: string;
  endAt: string;
  day: string;
  label: 'Best' | 'Good' | 'Limited';
  type: 'waiter' | 'dropoff';
  /** CORE-AR: opaque server token — technician and bay stay on the server. */
  slotToken?: string;
}

interface OptiQueueCalendarProps {
  bookingRequestId: string;
  onComplete: (date: string, slot?: TimeSlot) => void;
  onBack: () => void;
  preference: 'waiter' | 'dropoff' | 'either';
  shopTimezone: string;
}

export function OptiQueueCalendar({
  bookingRequestId,
  onComplete,
  onBack,
  preference,
  shopTimezone,
}: OptiQueueCalendarProps) {
  const [dayStats, setDayStats] = useState<DayStat[]>([]);
  const [isLoadingDays, setIsLoadingDays] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [waiterTimes, setWaiterTimes] = useState<TimeSlot[]>([]);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(clock.now());
  const [error, setError] = useState<string | null>(null);

  // Load day stats on mount
  useEffect(() => {
    async function loadDayStats() {
      setIsLoadingDays(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase.functions.invoke('optiqueue-get-day-stats', {
          body: { bookingRequestId }
        });

        if (fetchError) throw fetchError;

        setDayStats(data.days || []);
      } catch (err: any) {
        console.error('[OptiQueueCalendar] Failed to load day stats:', err);
        setError(err.message || 'Failed to load calendar');
      } finally {
        setIsLoadingDays(false);
      }
    }

    loadDayStats();
  }, [bookingRequestId]);

  // Build day stats map for quick lookup
  const dayStatsMap = useMemo(() => {
    const map = new Map<string, DayStat>();
    for (const stat of dayStats) {
      map.set(stat.date, stat);
    }
    return map;
  }, [dayStats]);

  // Get calendar days for current month
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Pad with empty days at start to align with weekday
    const startPadding = start.getDay();
    const paddedDays: (Date | null)[] = Array(startPadding).fill(null);
    
    return [...paddedDays, ...days];
  }, [currentMonth]);

  const getDotColor = (stat: DayStat | undefined) => {
    if (!stat || !stat.isBookable) return 'bg-muted';
    // CORE-AR: a day is either bookable or not; how busy the shop is stays internal.
    return 'bg-green-500';
  };

  const handleDayClick = async (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const stat = dayStatsMap.get(dateStr);
    
    if (!stat?.isBookable) return;
    
    setSelectedDate(dateStr);
    setError(null);

    if (preference === 'dropoff') {
      // Drop-off: immediately proceed with date-only
      onComplete(dateStr);
    } else {
      // Waiter: fetch times for this day
      setIsLoadingTimes(true);
      setWaiterTimes([]);

      try {
        const { data, error: fetchError } = await supabase.functions.invoke('optiqueue-get-waiter-times', {
          body: { bookingRequestId, date: dateStr }
        });

        if (fetchError) throw fetchError;

        setWaiterTimes(data.options || []);
        
        if (data.options?.length === 0) {
          setError('No times available for this day');
        }
      } catch (err: any) {
        console.error('[OptiQueueCalendar] Failed to load waiter times:', err);
        setError(err.message || 'Failed to load times');
      } finally {
        setIsLoadingTimes(false);
      }
    }
  };

  const handleTimeClick = (slot: TimeSlot) => {
    if (!selectedDate) return;
    onComplete(selectedDate, slot);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
    setSelectedDate(null);
    setWaiterTimes([]);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
    setSelectedDate(null);
    setWaiterTimes([]);
  };

  const tomorrow = addDays(clock.now(), 1);

  if (isLoadingDays) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-sm text-muted-foreground">Loading calendar...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="font-semibold text-lg">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <Button variant="ghost" size="icon" onClick={handleNextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-xs font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, idx) => {
          if (!day) {
            return <div key={`empty-${idx}`} className="aspect-square" />;
          }

          const dateStr = format(day, 'yyyy-MM-dd');
          const stat = dayStatsMap.get(dateStr);
          const isInMonth = isSameMonth(day, currentMonth);
          const isPast = isBefore(day, tomorrow);
          const isDisabled = !stat?.isBookable || isPast || !isInMonth;
          const isSelected = selectedDate === dateStr;

          return (
            <button
              key={dateStr}
              onClick={() => !isDisabled && handleDayClick(day)}
              disabled={isDisabled}
              className={`
                relative aspect-square flex flex-col items-center justify-center rounded-lg
                text-sm font-medium transition-colors
                ${isDisabled 
                  ? 'text-muted-foreground/50 cursor-not-allowed' 
                  : 'hover:bg-muted cursor-pointer'
                }
                ${isSelected ? 'bg-primary text-primary-foreground hover:bg-primary' : ''}
                ${isToday(day) && !isSelected ? 'border border-primary' : ''}
              `}
            >
              {/* Utilization dot */}
              {stat && isInMonth && !isPast && (
                <div className={`absolute top-1 left-1 w-2 h-2 rounded-full ${getDotColor(stat)}`} />
              )}
              
              {/* Day number */}
              <span>{format(day, 'd')}</span>
            </button>
          );
        })}
      </div>

      {/* Utilization Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span>Light</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span>Moderate</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-orange-500" />
          <span>Busy</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-muted" />
          <span>Full</span>
        </div>
      </div>

      {/* Waiter Times Panel (shown when date selected and preference is waiter) */}
      {selectedDate && preference !== 'dropoff' && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">
                Available times for {format(new Date(selectedDate + 'T12:00:00'), 'EEEE, MMMM d')}
              </h4>
            </div>

            {isLoadingTimes ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : waiterTimes.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No waiter times available for this day
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {waiterTimes.map((slot, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleTimeClick(slot)}
                    className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 text-left transition-colors"
                  >
                    <div>
                      <div className="font-medium">
                        {format(new Date(slot.startAt), 'h:mm a')}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      slot.label === 'Best' 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                        : slot.label === 'Good' 
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    }`}>
                      {slot.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Back button */}
      <Button variant="outline" onClick={onBack} className="w-full">
        Back
      </Button>
    </div>
  );
}
